import { useCallback, useState } from 'react';
import { useApi, fetchApiRef } from '@backstage/frontend-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  type TrackDefinition,
  pulseTrackCreatePermission,
} from '@internal/backstage-plugin-pulse-common';
import { invalidateTracksCache } from './cache';

const errorFromResponse = async (
  response: Response,
  fallback: string,
): Promise<Error> => {
  try {
    const data = await response.json();
    return new Error(data.error ?? fallback);
  } catch {
    return new Error(fallback);
  }
};

/** Exposes whether the current user may create UI-managed Pulse tracks. */
export const useCreateTrack = () => {
  const { fetch } = useApi(fetchApiRef);
  const { allowed: canCreate } = usePermission({
    permission: pulseTrackCreatePermission,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const create = useCallback(
    async (track: TrackDefinition): Promise<TrackDefinition> => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('plugin://pulse/tracks', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(track),
        });
        if (!response.ok) {
          throw await errorFromResponse(
            response,
            `Failed to create track: ${response.status}`,
          );
        }
        const created = (await response.json()) as TrackDefinition;
        invalidateTracksCache();
        return created;
      } catch (error_) {
        const requestError = error_ as Error;
        setError(requestError);
        throw requestError;
      } finally {
        setLoading(false);
      }
    },
    [fetch],
  );

  return { canCreate, create, loading, error };
};
